import React, { useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { Image } from "react-bootstrap";


import { SingleConditionRow } from "components/campaign/campaignCreation/AdditionalQuestionBar";
import SinglePreviewCard from "components/campaignStreamline/SinglePreviewCard";
import BasePrompt from "components/base/prompt/BasePrompt";
import { DISPLAY_REWARD_LOGIC_TYPE } from "components/earning/RewardLogicSelector";
import { getDisplayRewardAmount } from "components/campaignStreamline/EarningRulePreviewCard";
import { EARNING_RULE_PERIODIC_TYPE } from "components/earning/PurchaseEarningRuleRewardLimitInputs";
import ContentSections from "components/base/ContentSections";
import { DISPLAY_EARNING_RULE_REWARD_TYPE, EarningRuleType, STAMP_CAMPAIGN_RULE_PAGE_ANCHOR_KEY } from "config/CustomEnums";
import { getReadablePeriod } from "utils/TimeFormatUtil";
import { earningRuleImageMap } from "./StampCampaignTasks";
import "./StampCampaignPreviewPropertiesCard.scss"
import CustomerEdit from 'assets/images/customer_edit.svg';
import { createAction } from "utils";

const displayEarningRuleType = {
  [EarningRuleType.generalPurchase]: "Purchase",
  [EarningRuleType.qrCodeScanning]: "QR code scanning",
  [EarningRuleType.gpsCheckIn]: "GPS check-in",
  [EarningRuleType.memberReferral]: "Referral",
}

const getEarningRuleCardInfo = (
  earningRuleData,
) => {
  const earningRuleType = earningRuleData?.type
  let requirements = [];

  let rewards = [
    {
      isMandatory: true,
      title: "Reward type",
      field: "displayRewardType",
      anchorKey: "reward_type_key",
    },
    {
      isMandatory: true,
      title: "Reward quantity",
      field: "displayStampRewardQuantity",
    },
  ];

  let limits = [
    {
      title: "Overall Limit (optional)",
      field: "overallLimit",
    },
    {
      title: "Periodic Limit (optional)",
      field: "displayPeriodicLimit",
    },
    {
      title: "Per head Limit (optional)",
      field: "perHeadLimit",
    },
    {
      title: "Per head periodic Limit (optional)",
      field: "displayPerHeadPeriodicLimit",
    },
  ];


  switch (earningRuleType) {
    case EarningRuleType.generalPurchase:
      requirements = [
        {
          title: "Minimum spending per transaction",
          field: "minSpending",
        },
        {
          title: "Minimum quantity per transaction",
          field: "minQuantity",
        },
        {
          title: "Accept receipt X days after purchase for system handling (optional)",
          field: "eligibleDays",
        },
        {
          title: "Upload receipt X days after purchase (optional)",
          field: "uploadEligibleDays",
        },
        {
          isBoolean: true,
          title: "Exclude delivery cost (for online purchase)",
          field: "isExcludeDeliveryCost",
        },
        {
          isBoolean: true,
          title: "Exclude other charges (for online purchase)",
          field: "isExcludeOtherCharges",
        },
        {
          isMandatory: true,
          isBoolean: true,
          title: "Line item requirement (optional)",
          field: "lineItemRequirement",
        },
        {
          isMandatory: true,
          title: "Line item requirement (optional)",
          field: "lineItemGroups",
        },
        {
          isMandatory: true,
          title: "Eligible stores",
          field: "readableSelectedStores",
        },
      ];
      rewards = [
        {
          isMandatory: true,
          title: "Reward logic",
          field: "displayRewardLogicType",
        },
        {
          isMandatory: true,
          title: "Reward type",
          field: "displayRewardType",
          anchorKey: "reward_type_key",
        },
        {
          isMandatory: true,
          title: "Reward amount",
          field: "displayRewardAmount",
        },
      ];
      limits = [
        {
          title: 'Maximum amount of rewards this earning rule can be issued in all time',
          field: 'displayEarningRuleAllTimeMaxAmount',
        },
        {
          title: 'Maximum number of times this earning rule can be triggered in all time',
          field: 'earningRuleAllTimeLimitMaxTriggerTimes',
        },
        {
          isBoolean: true,
          title: `[Earning rule]Time period - per ${earningRuleData?.earningRuleTimePeriodLimitPeriodicTypePerDays || "X"} days`,
          field: 'displayEarningRuleTimePeriodPerXDay',
        },
        {
          isBoolean: true,
          title: '[Earning rule]Time period - Specific start and end time',
          field: 'displayEarningRuleTimePeriodSpecialPeriod',
        },
        {
          isBoolean: true,
          title: '[Earning rule]Time period follow active period',
          field: 'earningRuleTimePeriodLimitPeriodicFollowActive',
        },
        {
          title: '[Earning rule]Customised time period',
          field: 'displayEarningRuleCustomisedTimePeriod',
        },
        {
          title: 'Maximum amount of rewards this earning rule can be issued within a specified time period',
          field: 'displayEarningRuleSpecialTimeMaxAmount',
        },
        {
          title: 'Maximum number of times this earning rule can be triggered within a specified time period',
          field: 'earningRuleTimePeriodLimitMaxTriggerTimes',
        },
        {
          title: 'Maximum amount of rewards this earning rule can be issued per member in all time',
          field: 'displayPerMemberAllTimeMaxAmount',
        },
        {
          title: 'Maximum number of times this earning rule can be triggered per member in all time',
          field: 'perMemberAllTimeLimitMaxTriggerTimes',
        },
        {
          isBoolean: true,
          title: `[Per member]Time period - per ${earningRuleData?.perMemberTimePeriodLimitPeriodicTypePerDays || "X"} days`,
          field: 'displayPerMemberTimePeriodPerXDay',
        },
        {
          isBoolean: true,
          title: '[Per member]Time period - Specific start and end time',
          field: 'displayPerMemberTimePeriodSpecialPeriod',
        },
        {
          isBoolean: true,
          title: '[Per member]Time period follow active period',
          field: 'displayPerMemberTimePeriodFollowActivePeriod',
        },
        {
          title: '[Per member]Customised time period',
          field: 'displayPerMemberCustomisedTimePeriod',
        },
        {
          title: 'Maximum amount of rewards this earning rule can be issued per member within a specified time period',
          field: 'displayPerMemberSpecialTimeMaxAmount',
        },
        {
          title: 'Maximum number of times this earning rule can be triggered per member within a specified time period',
          field: 'perMemberTimePeriodLimitMaxTriggerTimes',
        },
        {
          title: 'Maximum amount of rewards this earning rule can be issued per transaction',
          field: 'displayPerTransactionMaxAmount',
        },
      ];
      break;
    case EarningRuleType.qrCodeScanning:
      requirements = [
        {
          isMandatory: true,
          title: "QR codes",
          field: "qrCodes",
        }
      ];
      break
    case EarningRuleType.gpsCheckIn:
      requirements = [
        {
          isMandatory: true,
          title: "Latitude",
          field: "latitude",
        },
        {
          isMandatory: true,
          title: "Longitude",
          field: "longitude",
        },
        {
          isMandatory: true,
          title: "Radius",
          field: "displayRadius",
        }
      ];
      break
    case EarningRuleType.memberReferral:
      requirements = [
        {
          isMandatory: true,
          title: "Beneficiary",
          field: "referralType",
        },
      ];
      if (earningRuleData?.referralType !== "INVITEE") {
        limits = [
          {
            isMandatory: true,
            title: "Maximum number of invitees (optional, for inviters only)",
            field: "maxInvitees",
          },
        ];
      } else {
        limits = []
      }

      break
    default:
      break;
  };


  const cardInfo = [
    {
      sectionTitle: "Properties",
      sectionContent: [],
    },
    {
      sectionTitle: "Rules",
      sectionContent: [
        {
          isMandatory: true,
          title: "Rule type",
          field: "readableType",
        },
      ],
    },
    {
      sectionTitle: "GeneraL",
      sectionContent: [
        {
          isMandatory: true,
          haveTranslation: true,
          title: "name",
          field: "generalName",
        },
      ]
    },
    {
      sectionTitle: "REQUIREMENTS",
      sectionContent: requirements,
    },
    {
      sectionTitle: "REWARDS",
      sectionContent: rewards,
    },
    ...([
      EarningRuleType.generalPurchase,
      EarningRuleType.memberReferral,
    ].includes(earningRuleType) ? [
      {
        sectionTitle: "EXECUTION PROIORITY FOR GLOBAL LIMIT",
        sectionContent: [
          {
            isMandatory: true,
            title: "Execution priority",
            field: "executionPriority",
          },
        ]
      },
    ] : []),
    ...(earningRuleData?.haveLimitsField && limits?.length > 0 ? [
      {
        sectionTitle: "LIMITS",
        sectionContent: limits,
      },
    ] : []),
  ];

  return cardInfo

}

const EarningRulePreviewPrompt = ({
  languages,
  earningRule,
  show,
  setShow,
}) => {
  const closePrompt = () => setShow(false);

  const earningRuleData = {
    ...earningRule,
    readableType: displayEarningRuleType?.[earningRule?.type] || earningRule?.type,
    displayRadius: `${earningRule?.radius || "-"} meters`,
    readableSelectedStores: earningRule?.selectedStores?.map(item => item.displayStoreName),

    // Reward
    displayRewardLogicType: earningRule?.lineItemRequirement
      ? DISPLAY_REWARD_LOGIC_TYPE?.[earningRule?.rewardLogicType] || ""
      : DISPLAY_REWARD_LOGIC_TYPE.TRANSACTION_VALUE,
    displayRewardAmount: getDisplayRewardAmount(earningRule),

    displayRewardType: DISPLAY_EARNING_RULE_REWARD_TYPE?.[earningRule?.rewardType] || earningRule?.rewardType,
    displayStampRewardQuantity: `${earningRule?.quantity || 0} stamp per action`,
    // Limit
    displayPeriodicLimit:
      earningRule?.periodicLimit && earningRule?.periodicLimitDays
        ? `${earningRule?.periodicLimit} times per ${earningRule?.periodicLimitDays} days`
        : '',
    displayPerHeadPeriodicLimit:
      earningRule?.perHeadPeriodicLimit && earningRule?.perHeadPeriodicLimitDays
        ? `${earningRule?.perHeadPeriodicLimit} times per ${earningRule?.perHeadPeriodicLimitDays} days`
        : '',

    displayEarningRuleAllTimeMaxAmount:
      `${earningRule?.earningRuleAllTimeLimitCoupons || '-'} coupons\n` +
      `${earningRule?.earningRuleAllTimeLimitPoints || '-'} points\n` +
      `${earningRule?.earningRuleAllTimeLimitPp || '-'} progression points\n` +
      `${earningRule?.earningRuleAllTimeLimitStamps || '-'} stamps`,
    displayEarningRuleTimePeriodPerXDay:
      earningRule?.earningRuleTimePeriodLimitPeriodicType === EARNING_RULE_PERIODIC_TYPE.perDays,
    displayEarningRuleTimePeriodSpecialPeriod:
      earningRule?.earningRuleTimePeriodLimitPeriodicType === EARNING_RULE_PERIODIC_TYPE.specificTime,
    displayEarningRuleCustomisedTimePeriod: getReadablePeriod(
      earningRule.earningRuleTimePeriodLimitPeriodicStartTime,
      earningRule.earningRuleTimePeriodLimitPeriodicEndTime,
      earningRule.earningRuleTimePeriodLimitPeriodicFollowActive,
      "-"
    ),
    displayEarningRuleSpecialTimeMaxAmount:
      `${earningRule?.earningRuleTimePeriodLimitCoupons || '-'} coupons\n` +
      `${earningRule?.earningRuleTimePeriodLimitPoints || '-'} points\n` +
      `${earningRule?.earningRuleTimePeriodLimitPp || '-'} progression points\n` +
      `${earningRule?.earningRuleTimePeriodLimitStamps || '-'} stamps`,
    displayPerMemberAllTimeMaxAmount:
      `${earningRule?.perMemberAllTimeLimitCoupons || '-'} coupons\n` +
      `${earningRule?.perMemberAllTimeLimitPoints || '-'} points\n` +
      `${earningRule?.perMemberAllTimeLimitPp || '-'} progression points\n` +
      `${earningRule?.perMemberAllTimeLimitStamps || '-'} stamps`,
    displayPerMemberTimePeriodPerXDay:
      earningRule?.perMemberTimePeriodLimitPeriodicType === EARNING_RULE_PERIODIC_TYPE.perDays,
    displayPerMemberTimePeriodSpecialPeriod:
      earningRule?.perMemberTimePeriodLimitPeriodicType === EARNING_RULE_PERIODIC_TYPE.specificTime,
    displayPerMemberTimePeriodFollowActivePeriod: earningRule?.perMemberTimePeriodLimitPeriodicFollowActivePeriod,
    displayPerMemberCustomisedTimePeriod: getReadablePeriod(
      earningRule.perMemberTimePeriodLimitPeriodicStartTime,
      earningRule.perMemberTimePeriodLimitPeriodicEndTime,
      earningRule.perMemberTimePeriodLimitPeriodicFollowActivePeriod,
      "-"
    ),
    displayPerMemberSpecialTimeMaxAmount:
      `${earningRule?.perMemberTimePeriodLimitCoupons || '-'} coupons\n` +
      `${earningRule?.perMemberTimePeriodLimitPoints || '-'} points\n` +
      `${earningRule?.perMemberTimePeriodLimitPp || '-'} progression points\n` +
      `${earningRule?.perMemberTimePeriodLimitStamps || '-'} stamps`,
    displayPerTransactionMaxAmount:
      `${earningRule?.perTransactionLimitCoupons || '-'} coupons\n` +
      `${earningRule?.perTransactionLimitPoints || '-'} points\n` +
      `${earningRule?.perTransactionLimitPp || '-'} progression points\n` +
      `${earningRule?.perTransactionLimitStamps || '-'} stamps`,
  }

  const getLanguageTabs = (language) => ([
    <SinglePreviewCard
      language={language}
      data={earningRuleData}
      cardInfo={getEarningRuleCardInfo(earningRuleData)}
      hideEditButton
    />])

  return <BasePrompt
    show={show}
    disableEnforceFocus
    customClass=""
    title={"View task"}
    otherBody={() =>
      <ContentSections
        hidePreview
        contentStyle="no-margin-bottom"
        languageTabContent={{
          containers: languages?.map((language) => {
            return {
              container: getLanguageTabs(language.code),
              key: language.code,
              title: language.sourceName,
            };
          }) || [],
        }}
      />
    }
    disableClickBackground={true}
    closeAction={closePrompt}
    rightButton={{
      text: 'OK',
      action: closePrompt,
    }}
  />
}

const StampCampaignPreviewPropertiesCard = ({
  errors,
  campaignData,
  languages,
  hideEditButton = false,
}) => {
  const dispatch = useDispatch();
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const displayTaskData = useRef();

  const indexOfErrorStampTasks = campaignData?.stampTasks?.filter(
    (value, index) => errors?.some(err => err?.startsWith(`stampTasks.${index}.`))
  )?.map((item, index) => index) || []

  return (
    <>
      <div id={STAMP_CAMPAIGN_RULE_PAGE_ANCHOR_KEY.tasks.sectionKey} />
      <label
        className="create-section-title single-preview-card-section-title"
      >
        {"TASKS"}
      </label>
      {campaignData?.stampTasks?.map((earningRule, index) =>
        <SingleConditionRow
          customClassName={"stamp-task-row"}
          isError={indexOfErrorStampTasks.includes(index)}
          image={earningRuleImageMap?.[earningRule?.type] || null}
          title={earningRule?.translations?.en?.generalName || earningRule?.name}
          extraActionButton={<>
            <button
              className="delete-condition-group-button"
              type="button"
              onClick={() => {
                displayTaskData.current = earningRule;
                setShowTaskDetail(true)
              }}
            >
              {"View task"}
            </button>
            {
              !hideEditButton && (
                <button
                  className="delete-condition-group-button"
                  type="button"
                  onClick={() => {
                    dispatch(createAction("campaignStreamline/stepChange")(
                      {
                        step: 1,
                        anchorKey: STAMP_CAMPAIGN_RULE_PAGE_ANCHOR_KEY.tasks.sectionContent.stamp_tasks_key,
                        sectionKey: STAMP_CAMPAIGN_RULE_PAGE_ANCHOR_KEY.tasks.sectionKey
                      }
                    ))
                  }}
                >
                  <Image src={CustomerEdit} className="content-delete-button" />
                  {"Edit"}
                </button>
              )
            }
          </>}
        />
      )
      }
      <EarningRulePreviewPrompt
        languages={languages}
        show={showTaskDetail}
        setShow={setShowTaskDetail}
        earningRule={displayTaskData.current || {}}
      />
    </>
  )
}

const mapPropsToState = (state) => ({
  errors: state.createCampaign.errorFields,
  languages: state.language.allList,
});

export default connect(mapPropsToState)(StampCampaignPreviewPropertiesCard);